import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Settings, Trash2 } from 'lucide-react';
import { SITE_STATUS } from '../../constants/siteStatus';
import { useAppCache } from '@/contexts/AppCacheContext';

interface SiteActionsDropdownProps {
  site: {
    id: string;
    status: string;
    url?: string;
    subdomain?: string;
  };
  // Callbacks
  onEdit?: () => void;
  onConfigure?: () => void;
  onDelete?: () => void;
  // Options
  showEditOption?: boolean;
  triggerVariant?: 'icon' | 'button';
  // When the dropdown sits on a dark surface (e.g. the SiteCard footer), the
  // default `variant="primary"` button reads as black-on-black. Inverted
  // flips the trigger to a light-on-dark pill.
  inverted?: boolean;
  // Demote the button trigger from primary to tertiary. Used in headers
  // that already render a primary CTA (e.g. Publish), so the row keeps
  // exactly one primary affordance.
  buttonTone?: 'primary' | 'tertiary';
  // Loading states
  isDeleting?: boolean;
}

/**
 * Reusable dropdown menu for site actions
 * Can be used from SiteCard (list) and SiteDetail (detail page)
 */
export const SiteActionsDropdown: React.FC<SiteActionsDropdownProps> = ({
  site,
  onEdit,
  onConfigure,
  onDelete,
  showEditOption = true,
  triggerVariant = 'icon',
  inverted = false,
  buttonTone = 'primary',
  isDeleting = false,
}) => {
  const { hasPermission } = useAppCache();
  // const canCreate = hasPermission('create:sites'); // Future use
  const canUpdate = hasPermission('update:sites');
  const canDelete = hasPermission('delete:sites');

  // "Visit site" is no longer in this dropdown — that affordance lives next
  // to Actions in the site detail header and as an inline icon link on
  // SiteCard. The dropdown owns site-level transitions only (Edit, Delete).
  // Delete is hidden only while a destroy workflow is in flight — Delete
  // during a destroy is meaningless since the row is already being torn down.
  const showDelete = site.status !== SITE_STATUS.DESTROYING;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {triggerVariant === 'icon' ? (
          <Button size="icon" variant="tertiary" title="More actions" className="h-8 w-8 hover:no-underline">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        ) : inverted ? (
          <Button
            size="dense"
            variant="secondary"
            className="hover:bg-surface active:bg-surface active:text-foreground"
          >
            <MoreHorizontal />
            Manage site
          </Button>
        ) : (
          <Button size="default" variant={buttonTone}>
            <MoreHorizontal />
            Manage site
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* Edit */}
        {showEditOption && onEdit && canUpdate && (
          <DropdownMenuItem onClick={onEdit}>
            <Pencil />
            Edit
          </DropdownMenuItem>
        )}

        {/* Configure */}
        {onConfigure && canUpdate && (
          <DropdownMenuItem onClick={onConfigure}>
            <Settings />
            Configure
          </DropdownMenuItem>
        )}

        {/* Subscription actions live inline in the SiteDetail Subscription
            KV row. The dropdown only owns site-level transitions (Edit,
            Visit, Delete) — not billing. Re-publish lives in the header CTA
            for failed/stalled states; Clone is not exposed yet. */}

        {/* Delete — opens the DeleteSiteDialog which handles the active-sub
            branch on its own. Hidden during in-flight workflow runs to avoid
            racing the workflow and orphaning cloud state. */}
        {onDelete && canDelete && showDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              disabled={isDeleting}
              className="text-red-400 data-[highlighted]:bg-red-500/15 data-[highlighted]:text-red-300"
            >
              <Trash2 />
              {isDeleting ? 'Deleting...' : 'Delete site'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SiteActionsDropdown;
